import {
  trackCheckoutClickToLoginEvent,
  trackLoggedIn
} from '@ecomm/cdp-tracking'
import { useTrackCheckoutLeadOptedIn } from '@ecomm/checkout/shipping-hooks'
import type { CheckoutFormValuesType } from '@ecomm/checkout/shipping-schema'
import { getReturningCustomerAddressByDeviceId } from '@ecomm/checkout/utils'
import { useSetYoda } from '@ecomm/shared-cookies'
import { type LoginStatus, useTrackingLoggedIn } from '@ecomm/tracking'
import { type MaybeT, overloadMaybe } from '@simplisafe/ewok'
import type { UserCheckoutData } from '@simplisafe/ss-ecomm-data/simplisafe/yodaClient'
import { useFormikContext } from 'formik'
import * as E from 'fp-ts/lib/Either'
import { pipe } from 'fp-ts/lib/function'
import { useCallback, useState } from 'react'

import { FormCheckbox } from '../FormCheckbox'
import { FormField } from '../FormField'
import { FormTextInput } from '../FormTextInput'

type Props = {
  readonly hasCheckbox: boolean
}

export function Email({ hasCheckbox }: Props) {
  const [loginSuccess, setLoginSuccess] = useState(false)
  const {
    values: { offerAndTip }
  } = useFormikContext<CheckoutFormValuesType>()

  const setYoda = useSetYoda()

  const trackGTMLoggedIn = useTrackingLoggedIn()
  useTrackCheckoutLeadOptedIn(offerAndTip)

  const onLogin = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      trackCheckoutClickToLoginEvent()

      const trackCheckout = (status: LoginStatus) => {
        trackGTMLoggedIn(status, 'CHECKOUT')
        trackLoggedIn({ status, location: 'CHECKOUT' })
      }

      const handleError = (err: Error) =>
        err.message === 'Popup closed'
          ? trackCheckout('INVALID_OR_CLOSED')
          : err.message === 'Timeout'
            ? trackCheckout('TIMEOUT')
            : trackCheckout('API_FAILURE')

      const handleSuccess = (userCheckoutData: MaybeT<UserCheckoutData>) => {
        pipe(
          overloadMaybe(userCheckoutData),
          E.fromOption<LoginStatus>(() => 'NO_DATA'),
          E.chain(checkoutData => {
            setYoda({
              token: checkoutData.accessToken,
              userId: checkoutData.id,
              email: checkoutData.emailAddress
            })
            return E.left<LoginStatus, never>('SUCCESS')
          }),
          E.mapLeft(trackCheckout)
        )
        setLoginSuccess(true)
      }

      getReturningCustomerAddressByDeviceId(e, handleError, handleSuccess)
    },
    [setYoda, trackGTMLoggedIn]
  )

  return (
    <fieldset className="mx-0 border-0 p-0">
      <div className="mt-6 lg:mt-8">
        <legend className="flex w-full shrink-0 items-center whitespace-nowrap text-xl font-medium md:text-2xl my-5">
          Email
        </legend>
      </div>
      <div className="rounded-base bg-neutral-light-50 w-full p-4 pb-4 md:px-8 md:pb-8 border-0 mx-0">
        <div className="lg:flex lg:flex-row lg:gap-8">
          <div className="flex w-full grow !grid-cols-1 flex-col gap-1 prose-a:text-complementary-blue-150">
            {loginSuccess ? (
              <h3>Order info will be sent to the email address:</h3>
            ) : (
              <p>
                Enter a valid email address for this order or{' '}
                <a href="#" onClick={onLogin} role="button">
                  login with an existing account
                </a>{' '}
                and return to checkout.
              </p>
            )}
          </div>
          <FormField
            className="w-full md:w-1/2 lg:w-full"
            id="email"
            label="Email address*"
            labelClassName="mb-2 md:mb-1"
          >
            <FormTextInput
              className="w-full"
              id="email"
              name="email"
              placeholder="Email Address"
            />
          </FormField>
        </div>
        <hr
          className="border-neutral-medium-100 my-4 border border-b-0 border-solid"
          data-component="Divider"
        />
        {hasCheckbox ? (
          <FormField id="exclusive-offers">
            <FormCheckbox
              className="text-base font-normal"
              label="Send me exclusive offers and safety tips from SimpliSafe"
              name="offerAndTip"
            />
          </FormField>
        ) : null}
      </div>
    </fieldset>
  )
}
